import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { getApiConfig } from "../utils/config";

let _tokenFetcher: () => Promise<string>;

// Define authentication-related endpoints that should use the public API
const AUTH_ENDPOINTS = [
  '/auth',
  '/oauth',
  '/login',
  '/logout',
  '/token',
  '/userinfo',
  '/.well-known',
  '/user',
  '/profile'
];

// Define public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  '/health',
  '/status',
  '/ping'
];

/**
 * Check if a URL matches a specific endpoint using proper path-based matching
 * @param url The URL to check
 * @param endpoint The endpoint pattern to match against
 * @returns true if the URL starts with the endpoint and is followed by a path boundary
 */
function matchesEndpoint(url: string, endpoint: string): boolean {
  // Remove leading slash from URL for consistent comparison
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;

  // Check if URL starts with the endpoint
  if (!cleanUrl.startsWith(endpoint)) {
    return false;
  }

  // If the endpoint is exactly the URL, it's a match
  if (cleanUrl === endpoint) {
    return true;
  }

  // Check if the next character after the endpoint is a path separator
  // This ensures we don't match partial paths (e.g., '/auth' shouldn't match '/authenticate')
  const nextChar = cleanUrl.charAt(endpoint.length);
  return nextChar === '/' || nextChar === '?' || nextChar === '#';
}

function getBaseUrlForRequest(url: string): string {
  const apiConfig = getApiConfig();

  // Check if this is an authentication-related request
  const isAuthRequest = AUTH_ENDPOINTS.some(endpoint =>
    matchesEndpoint(url, endpoint)
  );

  // Check if this is a public endpoint
  const isPublicRequest = PUBLIC_ENDPOINTS.some(endpoint =>
    matchesEndpoint(url, endpoint)
  );

  let selectedUrl: string;

  if (isAuthRequest) {
    selectedUrl = apiConfig.authApiUrl;
  } else if (isPublicRequest) {
    selectedUrl = apiConfig.publicApiUrl;
  } else {
    selectedUrl = apiConfig.privateApiUrl;
  }

  // Debug logging
  if (process.env.REACT_APP_DEBUG_API_ROUTING === 'true') {
    console.log(`API Routing Debug:`, {
      url,
      isAuthRequest,
      isPublicRequest,
      selectedUrl,
      authApiUrl: apiConfig.authApiUrl,
      publicApiUrl: apiConfig.publicApiUrl,
      privateApiUrl: apiConfig.privateApiUrl
    });
  }

  return selectedUrl;
}

const createApiService = () => {
  const apiService = axios.create({
    headers: {
      "Content-Type": "application/json",
    },
  });

  apiService.interceptors.request.use(
    async (config: AxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      config.headers = config.headers || {};

      // Set the base URL based on the request type
      const baseUrl = getBaseUrlForRequest(config.url || '');
      config.baseURL = baseUrl;

      if (!_tokenFetcher) {
        return Promise.resolve({
          ...config,
          // eslint-disable-next-line import/no-named-as-default-member
          cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
            cancel(
              "Axios API was not initialized with the appropriate token fetching function."
            )
          ),
        } as unknown as InternalAxiosRequestConfig);
      }

      try {
        const jwt = await _tokenFetcher();
        return Promise.resolve({
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${jwt}` },
        } as unknown as InternalAxiosRequestConfig);
      } catch (e) {
        return Promise.resolve({
          ...config,
          // eslint-disable-next-line import/no-named-as-default-member
          cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
            cancel("No access token!")
          ),
        } as unknown as InternalAxiosRequestConfig);
      }
    },
    (error: Error) => Promise.reject(error)
  );

  apiService.interceptors.response.use(
    (response: any) => {
      return response;
    },
    (error: any) => {
      return Promise.reject(error.response?.data || error.response);
    }
  );

  return apiService;
};

const apiService = createApiService();

export default apiService;

/**
 * Provide the ability to pass the getAccessTokenSilently function from Auth0 when the app bootstraps.
 * @param tokenFetcher
 */
export const setTokenFetcher = (tokenFetcher: () => Promise<string>) => {
  _tokenFetcher = tokenFetcher;
  return this;
};
